import {Perspective} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/layout/Perspective';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {action, computed, observable} from 'mobx';
import * as React from 'react';

export type PanelSetName = string;

export enum PanelKind
{
    FlowBlocks = 'FlowBlocks',
    FlowProperties = 'FlowProperties',
    FlowConsole = 'FlowConsole',
    FlowDebugData = 'FlowDebugData',
    FlowProblems = 'FlowProblems',
    FlowCodeEditor = 'FlowCodeEditor',
    SurfaceComponents = 'SurfaceComponents',
    SurfaceProperties = 'SurfaceProperties',
    SurfaceConsole = 'SurfaceConsole',
    SurfaceDataBinding = 'SurfaceDataBinding'
}

export class PanelLayoutState
{
    @observable
    public allPerspectives:Perspective[] = [];

    @observable
    public activePerspective:Perspective = null;

    @observable
    public panelsDomHost:HTMLElement = null;
}

export class PanelSet
{
    public readonly id:GUID = IdUtil.newGUID();

    public readonly glassLayers:GlassLayer[];

    @observable
    public isPanelAreasVisible:boolean = true; // SectionKind == PanelArea

    constructor(public readonly name:PanelSetName,
                public readonly rootRun:SectionRun,
                glasses:GlassLayer[] = [])
    {
        this.glassLayers = glasses;
    }
}

export class PanelBounds
{
    constructor(public readonly x:number,
                public readonly y:number,
                public readonly width:number,
                public readonly height:number)
    {
    }
}

export enum RunSpecMode
{
    Stretch = 'Stretch',
    FixedPixels = 'FixedPixels',
    Percent = 'Percent'
}

export class RunSpec
{
    @observable
    public value:number;

    private constructor(public readonly mode:RunSpecMode,
                        value:number)
    {
        this.value = value;
    }

    public get isStretch():boolean
    {
        return this.mode === RunSpecMode.Stretch;
    }

    public get isFixedPixels():boolean
    {
        return this.mode === RunSpecMode.FixedPixels;
    }

    public get isPercent():boolean
    {
        return this.mode === RunSpecMode.Percent;
    }

    public static stretch():RunSpec
    {
        return new RunSpec(RunSpecMode.Stretch, NaN);
    }

    public static px(exactPixels:number):RunSpec
    {
        return new RunSpec(RunSpecMode.FixedPixels, exactPixels);
    }

    public static percent(percentZeroToOne:number):RunSpec
    {
        return new RunSpec(RunSpecMode.FixedPixels, percentZeroToOne);
    }

    public makeCopy():RunSpec
    {
        return new RunSpec(this.mode, this.value);
    }
}

export interface ILayoutItem
{
    runSpec:RunSpec;
}

export class GlassLayer
{
    private constructor(public readonly id:GUID,
                        public readonly element:React.ReactElement<any>)
    {
    }

    public static of(element:React.ReactElement<any>):GlassLayer
    {
        const id = IdUtil.newGUID();
        return new GlassLayer(id, element);
    }
}

export class SectionRun implements ILayoutItem
{
    @observable
    public isHorizontal:boolean;

    @observable
    public sections:Section[];

    private constructor(public readonly id:GUID,
                        public readonly runSpec:RunSpec,
                        isHorizontal:boolean,
                        sections:Section[])
    {
        this.isHorizontal = isHorizontal;
        this.sections = sections;
    }

    public static horizontal(sections:Section[],
                             runSpec:RunSpec = null):SectionRun
    {
        const id = IdUtil.newGUID();

        if( !runSpec )
            runSpec = RunSpec.stretch();

        return new SectionRun(id, runSpec, true, sections);
    }

    public static vertical(sections:Section[],
                           runSpec:RunSpec = null):SectionRun
    {
        const id = IdUtil.newGUID();

        if( !runSpec )
            runSpec = RunSpec.stretch();

        return new SectionRun(id, runSpec, false, sections);
    }
}

export type SectionContent = PanelGroup | SectionRun;

export class SectionVisibility
{
    public static readonly Visible:SectionVisibility = new SectionVisibility('visible');
    public static readonly Collapsed:SectionVisibility = new SectionVisibility('collapsed');
    public static readonly Hidden:SectionVisibility = new SectionVisibility('hidden');

    private constructor(private _value:string)
    {
    }

    isEqualTo(other:SectionVisibility):boolean
    {
        return other && other._value === this._value;
    }
}

export enum SectionKind
{
    Content = 'Content',
    PanelArea = 'PanelArea'
}

export class Section implements ILayoutItem
{
    @observable
    public visibility:SectionVisibility = SectionVisibility.Visible;

    @observable
    public content:SectionContent = null;

    private constructor(public readonly id:GUID,
                        public readonly runSpec:RunSpec,
                        public readonly kind:SectionKind,
                        content:SectionContent | null)
    {
        this.content = content;
    }

    public static ofLength(lengthInPx:number,
                           kind:SectionKind,
                           content:Panel | SectionContent):Section
    {

        const runSpec = RunSpec.px(lengthInPx);
        return Section._create(kind, runSpec, content);
    }

    public static stretch(kind:SectionKind,
                          content:Panel | SectionContent):Section
    {
        const runSpec = RunSpec.stretch();
        return Section._create(kind, runSpec, content);
    }

    public static ofPercent(percent:number,
                            kind:SectionKind,
                            content:Panel | SectionContent):Section
    {
        const runSpec = RunSpec.percent(percent);
        return Section._create(kind, runSpec, content);
    }

    private static _create(kind:SectionKind, runSpec:RunSpec, content:Panel | SectionContent):Section
    {
        const id = IdUtil.newGUID();

        if( content instanceof Panel )
            return new Section(id, runSpec, kind, PanelGroup.ofOne(content as Panel));
        else if( content instanceof PanelGroup )
            return new Section(id, runSpec, kind, content);
        else if( content instanceof SectionRun )
            return new Section(id, runSpec, kind, content);
        else
            return new Section(id, runSpec, kind, null);
    }
}

export class HeavyPanelContent
{
    private constructor(public readonly forPanelKind:PanelKind,
                        public readonly content:React.ReactElement<any>)
    {
    }

    public static create(panelKind:PanelKind,
                         content:React.ReactElement<any>):HeavyPanelContent
    {
        return new HeavyPanelContent(panelKind, content);
    }
}

export type PanelGroupId = GUID;

export class PanelGroup
{
    @observable
    public selectedPanel:Panel = null;

    @observable
    public panels:Panel[];

    public heavyPanelContents:HeavyPanelContent[] = null;

    private constructor(public readonly id:PanelGroupId,
                        panels:Panel[] = [])
    {
        this.panels = panels;
    }

    public static of(panels:Panel[]):PanelGroup
    {
        const id = IdUtil.newGUID();
        const group = new PanelGroup(id, panels);

        PanelGroup._selectFirstPanel(group);

        return group;
    }

    public static ofOne(panel:Panel):PanelGroup
    {
        const id = IdUtil.newGUID();
        const group = new PanelGroup(id, [panel]);

        PanelGroup._selectFirstPanel(group);

        return group;
    }

    public selectFirstPanel():void
    {
        PanelGroup._selectFirstPanel(this);
    }

    @action
    private static _selectFirstPanel(group:PanelGroup):void
    {
        const panels = group.panels;
        group.selectedPanel = (panels && panels.length > 0) ? panels[0] : null;
    }
}

export type NoPanelTitle = null;
export type PanelContentFactoryFn = () => React.ReactElement<any> | null;
export type PanelId = GUID;

export type PanelTitleFn = () => (string);

export class PanelHeader
{
    @observable
    public title:string | PanelTitleFn | NoPanelTitle;

    @observable
    public icon:React.ReactElement<any> = null;

    public readonly isCloseable:boolean;
    public readonly isPinnable:boolean;

    @observable
    public isEnabled:boolean;

    @observable
    public isPinned:boolean;

    private constructor(title:string | PanelTitleFn | NoPanelTitle, // null for FlowCanvas,SurfaceArea, & other main content
                        isCloseable:boolean,
                        isPinnable:boolean,
                        isEnabled:boolean,
                        isPinned:boolean)
    {
        this.title = title;

        this.isCloseable = isCloseable;
        this.isPinnable = isPinnable;
        this.isEnabled = isEnabled;
        this.isPinned = isPinned;
    }

    public static forContent():PanelHeader
    {
        return new PanelHeader(null, false, false, true, false);
    }

    public static withTitle(title:string):PanelHeader
    {
        return new PanelHeader(title, false, false, true, false);
    }

    public static withPinnableTitle(title:string):PanelHeader
    {
        return new PanelHeader(title, true, true, true, false);
    }

    public static withCloseableTitle(title:string | PanelTitleFn):PanelHeader
    {
        return new PanelHeader(title, true, false, true, false);
    }

    public static newPanelTitleFn(getTitleFn:PanelTitleFn):PanelTitleFn
    {
        const panelTitleFn:PanelTitleFn = () =>
        {
            return computed(() => (getTitleFn())).get();
        };

        return panelTitleFn;
    }
}

export interface IPanelDatum
{
    isEqualTo(other:IPanelDatum):boolean
}

export class Panel
{
    @observable
    public datum:IPanelDatum = null;

    private constructor(public readonly id:PanelId,
                        public readonly kind:PanelKind,
                        public readonly panelGroup:PanelGroup,
                        public readonly header:PanelHeader,
                        public readonly contentFactoryFn:PanelContentFactoryFn)
    {
    }

    public getElement():JSX.Element
    {
        return this.contentFactoryFn();
    }

    public static ofContent(kind:PanelKind, panelGroup:PanelGroup, factoryFn:PanelContentFactoryFn):Panel
    {
        const id = IdUtil.newGUID();
        const header = PanelHeader.forContent();

        return new Panel(id, kind, panelGroup, header, factoryFn);
    }

    public static of(kind:PanelKind, panelGroup:PanelGroup, title:string, factoryFn:PanelContentFactoryFn):Panel
    {
        const id = IdUtil.newGUID();
        const header = PanelHeader.withTitle(title);

        return new Panel(id, kind, panelGroup, header, factoryFn);
    }

    public static ofHeader(panelId:PanelId, kind:PanelKind, panelGroup:PanelGroup, header:PanelHeader, factoryFn:PanelContentFactoryFn):Panel
    {
        return new Panel(panelId, kind, panelGroup, header, factoryFn);
    }

    public static ofWithId(panelId:PanelId, kind:PanelKind, panelGroup:PanelGroup, title:string, factoryFn:PanelContentFactoryFn):Panel
    {
        const header = PanelHeader.withTitle(title);
        return new Panel(panelId, kind, panelGroup, header, factoryFn);
    }
}








